import React, { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { PageMask } from 'components';
import 'theme/global.css';
import { useUserContext } from 'store';
import { extractMainUserData, fetchUserData } from 'utils/userFunctions';
import { Pages, initialPageId } from 'components/HeaderBottomAndLateral/Pages';
import { NoMatch } from 'pages/NoMatch';
import i18n from 'i18n';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { VisualizerContextProvider } from 'contexts/VisualizerContext';
import { ProfileContextProvider } from 'pages/ProfilePage/Context/ProfileContext';
import { PageMaskContextProvider } from 'contexts/PageMaskContext';
import { PrivacyContextProvider } from 'contexts/PrivacyContext';
import { EHRContextProvider } from 'contexts/EHRContext';
import { logDebug } from 'utils/AuxiliarFunctions';
import { SearchPageContextProvider } from 'pages/SearchPage/context/SearchPageContext';
import { HomePage } from 'pages/HomePage';
import { AccessHistoryContextProvider } from 'contexts/AccessHistoryContext';
import { RemoveConsentModalContextProvider } from 'pages/SharePage/Context/RemoveConsentModalContext';
import { NewConsentModalContextProvider } from 'pages/SharePage/Context/NewConsentModalContext';
import { RenewConsentModalContextProvider } from 'pages/SharePage/Context/RenewConsentModalContext';
import { TabAreaContextProvider } from 'pages/SharePage/Context/TabAreaContext';
import { ErrorPage } from 'pages/ErrorPage';
import { LoadingHU } from 'components/LoadingHU';
import { TeleconsultingContextProvider } from 'contexts/TeleconsultingContext';
import xdsToken from 'services/xdsToken';

export default function LoggedUserPortal() {
  const [user, setUser] = useUserContext();
  const [active, setActive] = useState(false);
  const [noError, setNoError] = useState(true);
  const [availablePages, setAvailablePages] = useState([]);
  const { t } = useTranslation('PageMask');
  // const [expiresIn, setExpiresIn] = useState(Number(sessionStorage.getItem('token_expiresIn')));
  // const refreshTime = expiresIn <= 3600 ? (expiresIn * 1000) * 0.75 : (3600 * 1000) * 0.75;

  async function renewToken() {
    await xdsToken.getTokenRefresh();
    // const refreshTokenExpiresIn = await retrieveData('token_expiresIn');
    // if (refreshTokenExpiresIn > 3600) setExpiresIn(3600);
    // setExpiresIn(Number(refreshTokenExpiresIn));
  }
  setInterval(() => {
    renewToken();
  }, 2700000);

  useEffect(() => {
    async function initializeUserContext() {
      try {
        const userDataResp = await fetchUserData();
        const extractedData = await extractMainUserData(userDataResp);
        const contextObject = { userData: userDataResp, ...extractedData };
        const activePages = Pages(contextObject.role).filter(
          (page) => page.active,
        );
        // Determina qual índice usar baseado no papel do usuário
        const indexKey = contextObject.role.isProfessional
          ? 'practitionerIndex'
          : 'normalIndex';
        // Ordena as páginas usando apenas o índice específico
        const sortedPages = activePages.sort((a, b) => {
          // Garante que undefined ou null sejam tratados como 99 (final da lista)
          const indexA = a[indexKey] ?? 99;
          const indexB = b[indexKey] ?? 99;
          return indexA - indexB;
        });
        setAvailablePages(sortedPages);
        setUser(contextObject);
        setActive(true);
      } catch (err) {
        logDebug(err);
        setNoError(false);
      }
    }
    if (!user.userData) initializeUserContext();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!active && noError) return <LoadingHU state />;
  if (!active && !noError) {
    return (
      <ErrorPage errorMessage="Ops! Houve um erro ao tentar efetuar o login no Portal." />
    );
  }

  return (
    <>
      <I18nextProvider i18n={i18n}>
        <PageMaskContextProvider availablePages={availablePages}>
          <EHRContextProvider>
            <VisualizerContextProvider>
              <PrivacyContextProvider>
                <ProfileContextProvider>
                  <SearchPageContextProvider>
                    <TabAreaContextProvider>
                      <RemoveConsentModalContextProvider>
                        <NewConsentModalContextProvider>
                          <RenewConsentModalContextProvider>
                            <AccessHistoryContextProvider>
                              <TeleconsultingContextProvider>
                                <Route
                                  exact
                                  path="/"
                                  component={() => {
                                    const InitialPage = availablePages.find(
                                      (page) => page.id === initialPageId,
                                    );
                                    const InitialPageComponent =
                                      InitialPage?.component || <HomePage />;
                                    return (
                                      <PageMask>
                                        {{
                                          content: () => (
                                            <InitialPageComponent />
                                          ),
                                          title: t(
                                            InitialPage?.title || 'Início',
                                          ),
                                        }}
                                      </PageMask>
                                    );
                                  }}
                                />
                                <Route
                                  path="/:pageId"
                                  component={({ match }) => {
                                    const { pageId } = match.params;
                                    const MatchPage = availablePages.find(
                                      (page) => page.id === pageId,
                                    );
                                    const MatchPageComponent =
                                      MatchPage?.component;
                                    return MatchPageComponent ? (
                                      <PageMask>
                                        {{
                                          content: () => <MatchPageComponent />,
                                          title: t(MatchPage.title),
                                        }}
                                      </PageMask>
                                    ) : (
                                      <PageMask>
                                        {{
                                          content: () => <NoMatch />,
                                          title: 'No Match',
                                        }}
                                      </PageMask>
                                    );
                                  }}
                                />
                              </TeleconsultingContextProvider>
                            </AccessHistoryContextProvider>
                          </RenewConsentModalContextProvider>
                        </NewConsentModalContextProvider>
                      </RemoveConsentModalContextProvider>
                    </TabAreaContextProvider>
                  </SearchPageContextProvider>
                </ProfileContextProvider>
              </PrivacyContextProvider>
            </VisualizerContextProvider>
          </EHRContextProvider>
        </PageMaskContextProvider>
      </I18nextProvider>
    </>
  );
}
